import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, CloudDownload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { cn } from "@/lib/utils";

const SfDatamSyncResultModal = ({
  open,
  onClose,
  summary,
  agents = [],
  isDark,
  textClass,
  labelClass,
  applying,
  onApplyMappings,
}) => {
  const [picks, setPicks] = useState({});

  const unmatched = useMemo(
    () => summary?.unmatched ?? [],
    [summary?.unmatched],
  );

  useEffect(() => {
    if (!open) {
      setPicks({});
      return;
    }
    const init = {};
    unmatched.forEach((u) => {
      init[u.sf_assistant_name] = null;
    });
    setPicks(init);
  }, [open, summary?.batch_id, unmatched]);

  const selectedCount = useMemo(
    () => Object.values(picks).filter((a) => a?.id).length,
    [picks],
  );

  const handleSaveMappings = useCallback(() => {
    const mappings = Object.entries(picks)
      .filter(([, agent]) => agent?.id)
      .map(([sf_assistant_name, agent]) => ({
        sf_assistant_name,
        agent_id: agent.id,
      }));
    if (!mappings.length) return;
    onApplyMappings?.(mappings);
  }, [picks, onApplyMappings]);

  if (!open || !summary) return null;

  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const blockClass = isDark ? "border-slate-700 bg-slate-950/50" : "border-gray-200 bg-gray-50/80";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "";
  const filterTriggerClass = cn(
    "h-9 min-h-9 w-full rounded-md border text-sm font-normal shadow-sm",
    isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-input bg-background",
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !applying && onClose()}>
      <DialogContent
        className={`max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${panelClass}`}
        onPointerDownOutside={(e) => {
          const t = e.target;
          if (t instanceof Element && t.closest("[data-radix-popover-content]")) {
            e.preventDefault();
          }
        }}
        onFocusOutside={(e) => {
          const t = e.target;
          if (t instanceof Element && t.closest("[data-radix-popover-content]")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <CloudDownload className={`w-5 h-5 shrink-0 ${isDark ? "text-sky-400" : "text-sky-600"}`} />
            Salesforce senkron sonucu
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 py-1 pr-1">
          <div className={`rounded-lg border p-3 ${blockClass}`}>
            <div className="flex items-start gap-2">
              <CheckCircle2 className={`w-5 h-5 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
              <div className={`text-sm space-y-1 ${textClass}`}>
                <p>
                  <strong>{summary.matched_count ?? 0}</strong> asistan kaydedildi.
                  {(summary.unmatched_count ?? 0) > 0 && (
                    <> · <strong>{summary.unmatched_count}</strong> isim eşleşmedi.</>
                  )}
                </p>
                <p className={`text-xs ${labelClass}`}>
                  {summary.report_name || "Datam raporu"}
                  {" · "}
                  {summary.start_date} – {summary.end_date}
                </p>
              </div>
            </div>
          </div>

          {unmatched.length > 0 && (
            <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-amber-700/50 bg-amber-950/20" : "border-amber-200 bg-amber-50/90"}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-5 h-5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-700"}`} />
                <p className={`text-sm font-medium ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                  İsteğe bağlı: panel agent seçerek eşleştirebilirsiniz. Eşleştirilmeyen isimler rapora dahil edilmez; «Devam et» ile kapatabilirsiniz.
                </p>
              </div>
              <ul className="space-y-3 max-h-[min(50vh,420px)] overflow-y-auto">
                {unmatched.map((u) => (
                  <li
                    key={u.sf_assistant_name}
                    className={`rounded-md border p-2 space-y-2 ${isDark ? "border-slate-700 bg-slate-950/40" : "border-amber-100 bg-white/80"}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className={`text-sm font-medium ${textClass}`}>{u.sf_assistant_name}</span>
                      <span className={`text-xs tabular-nums ${labelClass}`}>{u.column_total ?? "—"} kayıt</span>
                    </div>
                    <SearchableAgentSelect
                      agents={agents}
                      value={picks[u.sf_assistant_name] ?? null}
                      onChange={(agent) => setPicks((prev) => ({ ...prev, [u.sf_assistant_name]: agent }))}
                      placeholder="Panel agent seç…"
                      isDark={isDark}
                      inputClass={filterTriggerClass}
                      labelClass={labelClass}
                      size="default"
                      inDialog
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 shrink-0">
          {unmatched.length > 0 ? (
            <>
              {selectedCount > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={applying}
                  className="bg-sky-600 hover:bg-sky-500 text-white border-sky-600"
                  onClick={handleSaveMappings}
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {`${selectedCount} eşleştirmeyi kaydet`}
                </Button>
              ) : null}
              <Button type="button" disabled={applying} onClick={onClose}>
                Devam et
              </Button>
            </>
          ) : (
            <Button type="button" onClick={onClose}>Tamam</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SfDatamSyncResultModal;
